import type { Pipeline } from 'paintscout'
import React, { useEffect, useRef, useState } from 'react'
import { Card, makeStyles, Typography, InputField, Grid } from '@paintscout/ui'
import { EstimatorSelect, useClientOptions, DropdownSelect } from '@paintscout/ui-business'
import type { DropdownSelectOption } from '@paintscout/ui-business'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import type { Contact, Deal } from '@paintscout/api'
import SearchIcon from '@material-ui/icons/Search'
import { formatName, getDealStageOptions, getPipelines } from '@paintscout/util/builder'
import type { DashboardEstimator } from '@paintscout/util/builder'
import ContactSearch from 'src/components/common/ContactSearch'
import { useSnackbar } from 'notistack'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { addAuth0Prefix } from '@paintscout/util'
import classNames from 'classnames'
import ContactDialogButton from '../common/ContactDialogButton'
import LeadSourceSelector from '../dialogs/ContactDialog/LeadSourceSelector'
import { useDealValues } from './useDealValues'
import { useInitialOpenLoad } from './useInitialOpenLoad'
import { useContactAddressSearch } from './useContactAddressSearch'

const useStyles = makeStyles<Theme, DealFormProps>(
  (theme) => ({
    root: {},
    searchAdornment: {
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    dropdownRelative: {
      position: 'relative',
      cursor: 'pointer'
    },
    dropdown: {
      zIndex: 21474899999,
      position: 'absolute',
      boxShadow: theme.shadows[2],
      width: '98%'
    },
    dropdownItem: {
      padding: theme.spacing(1, 2),
      '&:hover': {
        outline: '1px solid #e0e0e0',
        backgroundColor: theme.palette.action.hover
      }
    }
  }),
  { name: 'DealForm' }
)

type FormProps = JSX.IntrinsicElements['form']

export interface DealFormProps extends Omit<FormProps, 'onSubmit'> {
  classes?: StyleClasses<typeof useStyles>
  isEditing: boolean
  onSubmit: (deal: Deal) => Promise<void>
  disabled?: boolean
  deal?: Partial<Deal>
  pipeline?: string
  defaultQuery?: string
  hideContact?: boolean
  hideProductionPipeline?: boolean
  defaultEstimator?: string
  onBack?: (event: React.MouseEvent<HTMLElement>) => void
  onCancel?: (event: React.MouseEvent<HTMLElement>) => void
  onDirtyChange?: (dirty: boolean) => void
}

export function filterPipelineOptions(pipelines: Pipeline[], hideProductionPipeline?: boolean) {
  return pipelines
    .filter((pipeline) => !pipeline.deleted)
    .filter((pipeline) => !(hideProductionPipeline && pipeline.key === 'production-pipeline'))
    .map((pipeline) => ({
      label: pipeline.label,
      value: pipeline.key
    }))
}

export function getShowPipelineSelect({
  hideProductionPipeline,
  multiPipelineFlag
}: {
  hideProductionPipeline?: boolean
  multiPipelineFlag: boolean
}) {
  return !hideProductionPipeline || multiPipelineFlag
}

function DealForm(props: DealFormProps) {
  const classes = useStyles(props)
  const {
    onSubmit,
    deal,
    disabled,
    isEditing,
    defaultQuery,
    hideContact,
    hideProductionPipeline,
    defaultEstimator,
    onBack,
    onCancel,
    onDirtyChange,
    ...baseFormProps
  } = props
  const { multiPipeline: multiPipelineFlag } = useFlags()
  const { options } = useClientOptions()
  const { enqueueSnackbar } = useSnackbar()
  const [isFirstSearch, setIsFirstSearch] = useState(true)

  const pipelines = getPipelines({ options })
  const pipelineOptions = filterPipelineOptions(pipelines, hideProductionPipeline)
  const showPipelineSelect = getShowPipelineSelect({ hideProductionPipeline, multiPipelineFlag })
  const initialPipelineKey = deal.pipeline?.value || props.pipeline || (!showPipelineSelect ? 'sales-pipeline' : '')

  const initialPipeline =
    pipelineOptions.find((pipeline) => pipeline.value === initialPipelineKey) || (isEditing ? null : pipelineOptions[0])

  const [pipeline, setPipeline] = useState<DropdownSelectOption>(initialPipeline)

  const [isContactSelected, setIsContactSelected] = useState(false)
  const [contactQuery, setContactQuery] = useState<string>('')
  const [jobAddressQuery, setJobAddressQuery] = useState<string>('')
  const {
    dealSources,
    dealValues,
    handleOwnerChange,
    handleContactChange,
    handleDropdownChange,
    handleFieldChange,
    handleSourceChange,
    mapAddressToValues,
    setFieldValue
  } = useDealValues({
    deal,
    options,
    isEditing
  })
  const initialValuesRef = useRef<string>(JSON.stringify(dealValues))
  const skipDirtyForPrefill = useRef(false)
  const hasPrefilledOwner = useRef(false)
  useEffect(() => {
    if (skipDirtyForPrefill.current) {
      initialValuesRef.current = JSON.stringify(dealValues)
      skipDirtyForPrefill.current = false
      onDirtyChange?.(false)
      return
    }
    onDirtyChange?.(JSON.stringify(dealValues) !== initialValuesRef.current)
  }, [dealValues, onDirtyChange])
  const [showNameWarning, setShowNameWarning] = useState(dealValues.name?.length > 50)

  const hasJobAddress = Object.values(dealValues.jobAddress || {}).some(Boolean)
  const hasOwner = Object.values(dealValues.owner || {}).some(Boolean)

  // update the job address query when the deal values change
  useEffect(() => {
    if (dealValues.jobAddress?.address || dealValues.jobAddress?.city) {
      const label = [dealValues.jobAddress.address, dealValues.jobAddress.city].filter((item) => item).join(', ')
      setJobAddressQuery(label)
    }
  }, [dealValues.jobAddress.address, dealValues.jobAddress.city])

  const dealStageOptions = pipeline ? getDealStageOptions({ options, pipeline: pipeline?.value }) : []
  const dealStageDropdownOptions = dealStageOptions.map((stage) => ({
    label: stage.label,
    value: stage.key
  }))

  const dealStage = dealValues.stage.value || dealStageOptions[0].key

  const handleSelectContact = (contact: Contact) => {
    setIsContactSelected(true)
    setContactQuery(formatName(contact))

    const currentId = dealValues.contact?.id
    // @ts-ignore
    const updatedId = contact.id || contact._id

    if (updatedId !== currentId) {
      handleContactChange(contact)
    }
  }

  const handleClearContact = () => {
    setIsContactSelected(false)
  }

  // Set default contact if provided
  useEffect(() => {
    if (deal?.contact?.id) {
      setIsContactSelected(true)
      setContactQuery(formatName(deal.contact))
    } else if (defaultQuery) {
      setContactQuery(defaultQuery)
    }
  }, [])

  const { isInitialLoad, isOpen, setIsOpen } = useInitialOpenLoad({
    initialLoad: true,
    initialOpen: false
  })

  const { addressSearchResults, debouncedAddressSearch } = useContactAddressSearch({
    options,
    isInitialLoad
  })

  const handleJobAddressSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setFieldValue({ field: 'jobAddress', value: '' })
    setJobAddressQuery(value)
    setIsOpen(true)
    debouncedAddressSearch(value)
  }

  const handleSubmit = async (event: React.FormEvent<HTMLElement>) => {
    event.preventDefault()

    if (!isContactSelected) {
      enqueueSnackbar('Please select a contact from the dropdown or create a new one', {
        variant: 'error'
      })
      return
    }

    const name = dealValues.name || dealValues.contact?.lastName || dealValues.contact?.firstName
    if (!name || name.length > 50) {
      setShowNameWarning(true)
      const errorMessage = !name ? 'Deal name is required' : 'Deal name cannot exceed 50 characters'
      enqueueSnackbar(errorMessage, {
        variant: 'error'
      })
      return
    }

    const now = Date.now()
    const deal: Deal = {
      ...dealValues,
      jobAddress: hasJobAddress ? dealValues.jobAddress : jobAddressQuery ? { address: jobAddressQuery } : {},
      owner: hasOwner ? dealValues.owner : null,
      stage: {
        value: dealStage
      },
      history:
        dealValues.history?.length > 0
          ? dealValues.history
          : [
              {
                enteredAt: now,
                exitedAt: null,
                stage: dealStage
              }
            ],
      pipeline: {
        value: pipeline.value,
        label: pipeline.label
      },
      enteredStage: dealValues.enteredStage || now,
      name,
      updated: now,
      created: dealValues.created || now,
      status: dealValues.status || ('draft' as Deal['status'])
    }

    onSubmit(deal)
  }

  // Safari-mobile safeguard: when ContactSearch / address (real text inputs)
  // are focused, iOS keeps the virtual keyboard up. The next tap on Confirm /
  // X / a dropdown trigger is consumed dismissing the keyboard instead of
  // firing as a click — the "needs two taps" behaviour from #20209 / 7.2.0.
  // Blur the focused input on form-level mousedown so the keyboard dismisses
  // synchronously and the click lands on the first tap. Restrict to real
  // editable inputs: the DropdownSelect trigger is readOnly + inputMode='none'
  // and never opens the keyboard, so blurring it on every tap (including taps
  // on options inside the open dropdown) is noise that re-introduces the
  // first-tap-eaten bug on options. Do NOT broaden without an equivalent fix.
  const handleFormMouseDown = (e: React.MouseEvent) => {
    const active = document.activeElement
    if (!(active instanceof HTMLInputElement)) return
    if (active.readOnly || active.getAttribute('inputmode') === 'none') return
    if (active === e.target || active.contains(e.target as Node)) return
    active.blur()
  }

  const commonInputProps = {
    // onChange: handleFieldChange,
    // margin: 'dense' as const,
    fullWidth: true,
    disabled,
    classes: { root: classes.textField }
  }

  const EstimatorSelectComponent = (
    <EstimatorSelect
      name="estimators"
      label="Owner"
      className={classes.dropdownSelect}
      fullWidth
      variant="single"
      searchable
      includeUser={(user) => user.app_metadata?.active !== false}
      roles={['admin', 'sales', 'viewer', 'core', 'team']}
      value={dealValues?.owner?.id ? addAuth0Prefix(dealValues.owner.id) : null}
      userFields={['user_metadata.playerName', 'picture', 'user_metadata.customAvatar.value']}
      onChange={async (estimatorOption: DashboardEstimator) => {
        handleOwnerChange(estimatorOption)
      }}
      autoApply
      showCollapsibleChips
      onFetched={
        defaultEstimator
          ? (estimatorOptions: DashboardEstimator[]) => {
              // One-time prefill only. EstimatorSelect re-fires onFetched on every
              // render (its options memo is unstable due to inline props), so
              // without this guard the default owner would be re-applied each
              // render and revert any owner the user manually picks (#20209).
              if (hasPrefilledOwner.current || dealValues.owner?.id) {
                return
              }
              const fullEstimator = estimatorOptions.find(
                (estimator) => estimator.value === addAuth0Prefix(defaultEstimator)
              )
              if (fullEstimator) {
                hasPrefilledOwner.current = true
                skipDirtyForPrefill.current = true
                handleOwnerChange(fullEstimator)
              }
            }
          : undefined
      }
    />
  )

  return (
    <form className={classes.root} onSubmit={handleSubmit} onMouseDown={handleFormMouseDown} {...baseFormProps}>
      <Grid container spacing={2}>
        {isEditing && (
          <Grid item xs={12} sm={showPipelineSelect ? 6 : 12}>
            <InputField
              {...commonInputProps}
              label="Deal Name"
              id="name"
              value={dealValues.name}
              onChange={(e) => {
                handleFieldChange(e)
                if (e.target.value.length > 50) {
                  setShowNameWarning(true)
                } else if (setShowNameWarning) {
                  setShowNameWarning(false)
                }
              }}
              required
              helperText={showNameWarning ? 'Deal name cannot exceed 50 characters' : ''}
              error={!dealValues.name || showNameWarning}
              showErrorBeforeValidation
            />
          </Grid>
        )}

        {showPipelineSelect && (
          <Grid item xs={12} sm={6}>
            {EstimatorSelectComponent}
          </Grid>
        )}

        {showPipelineSelect && (
          <Grid item xs={12} sm={6}>
            <DropdownSelect
              variant="single"
              value={pipeline}
              label="Pipeline"
              options={pipelineOptions}
              onChange={(value) => {
                setPipeline(value)
              }}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <DropdownSelect
            variant="single"
            value={{
              value: dealStage,
              label: dealStageOptions.find((stage) => stage.key === dealStage)?.label
            }}
            label="Stage"
            options={dealStageDropdownOptions}
            onChange={(option) => {
              handleDropdownChange({ id: 'stage.value', value: option.value })
            }}
          />
        </Grid>

        {!showPipelineSelect && (
          <Grid item xs={12} sm={6}>
            {EstimatorSelectComponent}
          </Grid>
        )}

        {!hideContact && (
          <>
            {isContactSelected ? (
              <Grid item xs={12} sm={showPipelineSelect ? 6 : 12}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <InputField
                    label="Contact"
                    value={formatName(dealValues.contact)}
                    onClick={() => handleClearContact()}
                    fullWidth
                    required
                    startAdornment={<div className={classes.searchAdornment}>{<SearchIcon />}</div>}
                  />
                  <ContactDialogButton
                    contactId={dealValues.contact.id}
                    label={'Edit'}
                    onConfirm={handleSelectContact}
                    disableDelete
                    variant={'dialog'}
                    size={'medium'}
                    inputHeight
                    noIcon
                    onBack={onBack}
                    onCancel={onCancel}
                  />
                </div>
              </Grid>
            ) : (
              <Grid item xs={12} sm={showPipelineSelect ? 6 : 12}>
                <ContactSearch
                  oversized
                  onSelectContact={(contact) => handleSelectContact(contact)}
                  integrations={{ paintscout: true }}
                  query={contactQuery}
                  isFirstSearch={isFirstSearch}
                  setIsFirstSearch={setIsFirstSearch}
                  onDialogBack={onBack}
                  onDialogCancel={onCancel}
                  showCreateNew={true}
                />
              </Grid>
            )}
          </>
        )}
        {dealValues.contact.id && (
          <>
            <Grid item xs={12} sm={6} className={classes.dropdownRelative}>
              <InputField
                {...commonInputProps}
                autoComplete={'off'}
                inputProps={{
                  role: 'presentation'
                }}
                id="address"
                label={'Location'}
                placeholder={'Enter address'}
                fullWidth={true}
                value={jobAddressQuery}
                onChange={handleJobAddressSearch}
                onClick={() => setIsOpen(true)}
                onBlur={() => setIsOpen(false)}
              />
              {addressSearchResults.length > 0 && isOpen && (
                <Card className={classes.dropdown}>
                  {addressSearchResults.map((result, i) => {
                    const label = [result.address, result.municipality].filter((item) => item).join(', ')
                    const label2 = [result.region, result.postalCode].filter((item) => item).join(', ')

                    return (
                      <div
                        key={i}
                        onMouseDown={() => {
                          setIsOpen(false)
                          mapAddressToValues(result)
                          setJobAddressQuery(label)
                        }}
                        className={classNames({
                          [classes.dropdownItem]: true,
                          [`address-result-${i}`]: true
                        })}
                      >
                        <Typography variant={'body1'}>{label}</Typography>
                        <Typography variant={'subtitle2'}>{label2}</Typography>
                      </div>
                    )
                  })}
                </Card>
              )}
            </Grid>
            <Grid item xs={12} sm={!isEditing ? 6 : showPipelineSelect ? 12 : 6}>
              <LeadSourceSelector
                label="Deal Source"
                value={{
                  value: dealValues.lead_source ? dealValues.lead_source : '',
                  label: dealValues.lead_source
                    ? dealSources.find(
                        (source) =>
                          source.label.trim() === dealValues.lead_source.trim() ||
                          source.name === dealValues.lead_source.trim()
                      )?.label
                    : ''
                }}
                sources={dealSources.map((source) => ({
                  value: source.name,
                  label: source.label
                }))}
                onChange={handleSourceChange}
              />
            </Grid>
          </>
        )}
      </Grid>
    </form>
  )
}

export default DealForm
